import { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { makeStyles } from '@fluentui/react-components';
import { KioskProvider, useKiosk } from '@features/kiosk/context/KioskContext';
import { useKioskHub } from '@shared/hooks/useSignalR';
import { KioskSetup } from '@features/kiosk/components/KioskSetup';
import { useKioskSessionStore } from '@features/kiosk/stores/kioskSessionStore';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
});

/**
 * SignalR handler — connects to BFF KioskHub at /bff/hubs/kiosk.
 * Receives safety training triggers pushed by the BFF when reception
 * calls the start-safety-training endpoint.
 *
 * Phase 34 plan 34-01: also handles the cross-process kiosk push rail's browser end. On a
 * `WaitingForInput` push, stores the payload and navigates to the generic waiting-for-input
 * dispatcher; on `Failed`, stores the payload too so `KioskWaitingForInput`'s fail-safe
 * `KioskUnknownState` branch renders a visible terminal screen — this is the stated resolution of
 * 34-RESEARCH.md Open Question 3: a failed instance gets its own push and its own visible
 * terminal screen, since leaving the visitor on a screen that will never advance is what
 * CONTEXT.md forbids outright.
 *
 * Phase 35 plan 35-12: on `Completed`, the payload is kept (not cleared) and the kiosk navigates
 * to the dedicated confirmation screen instead of straight back to idle — `KioskConfirmation`
 * reads the visitor's/host's names off this same payload and self-dismisses to `/kiosk` after its
 * own 15-second timer.
 *
 * Phase 35 plan 35-16 (Defect B gap closure): this component merges what used to be two separate
 * components (`KioskSignalRHandler` and `KioskWorkflowPushHandler`), each of which called the
 * kiosk-hub hook independently and therefore opened its OWN `HubConnection` (`useSignalR` builds a
 * fresh connection per call). Both connections independently invoked `RegisterKiosk`, putting two
 * connection ids into the same server-side group, so a `Clients.Group(...).SendAsync("workflowPush",
 * ...)` could land on the connection that only had a `startSafetyTraining` handler registered,
 * producing the observed browser warning `No client method with the name 'workflowpush' found.`
 * (35-16 Task 1 confirmed this structurally as B1 — the warning is fully explained by the
 * handler-less connection receiving the group message; whether the dual-connection setup could
 * ALSO drop a push outright during a `RegisterKiosk`/reconnect race — B2 — was not independently
 * reproduced and remains an unconfirmed, plausible contributing mechanism). A single connection
 * removes both the warning and that race entirely. The two `useEffect` calls below are kept
 * separate (not fused into one) — each handler's own subscribe/unsubscribe stays independent, the
 * only change is that both now read off the SAME `useKioskHub` call.
 */
function KioskHubHandler() {
  const navigate = useNavigate();
  const {
    kioskKey,
    setTriggeredTraining,
    setSafetyTrainingId,
    setSelectedVisitor,
    setActivePush,
    setSelfCheckInOrigin,
  } = useKiosk();

  const { onStartSafetyTraining, onWorkflowPush, isConnected } = useKioskHub(kioskKey ?? '');

  useEffect(() => {
    if (!isConnected || !kioskKey) return;

    const unsubscribe = onStartSafetyTraining((params) => {
      console.log('Received startSafetyTraining:', params);

      // GAP-04: this IS the reception-triggered journey — reception, not this kiosk, owns the
      // follow-up check-in call once the briefing is done. Clear defensively in case a PRIOR
      // self-check-in journey on this same device left its origin marker standing (KioskIndex's
      // own mount effect and KioskCancelDialog's confirm already clear it via reset(), but this
      // handler does not depend on either having run first).
      setSelfCheckInOrigin(null);
      setTriggeredTraining(params);
      setSafetyTrainingId(params.safetyTrainingId);

      setSelectedVisitor({
        id: params.personId,
        firstName: params.personGivenName,
        lastName: params.personSurname,
        company: '',
        email: '',
        status: 'expected' as const,
        safetyTrainingCompleted: false,
        createdAt: new Date().toISOString(),
      });

      navigate('/kiosk/checkin/safety-training');
    });

    return unsubscribe;
  }, [
    isConnected,
    kioskKey,
    onStartSafetyTraining,
    setTriggeredTraining,
    setSafetyTrainingId,
    setSelectedVisitor,
    setSelfCheckInOrigin,
    navigate,
  ]);

  useEffect(() => {
    if (!isConnected || !kioskKey) return;

    const unsubscribe = onWorkflowPush((payload) => {
      if (payload.status === 'WaitingForInput') {
        setActivePush(payload);
        navigate('/kiosk/step');
      } else if (payload.status === 'Completed') {
        setActivePush(payload);
        navigate('/kiosk/confirmation');
      } else {
        setActivePush(payload);
        navigate('/kiosk/step');
      }
    });

    return unsubscribe;
  }, [isConnected, kioskKey, onWorkflowPush, setActivePush, navigate]);

  return null;
}

function KioskLayoutContent() {
  const styles = useStyles();
  const { kioskKey } = useKiosk();
  const [isSetupComplete, setIsSetupComplete] = useState(!!kioskKey);
  // Quick task 260907-hmv: a session that cannot be renewed is a SEPARATE fact from
  // "never authenticated" — see kioskSessionStore.ts. A `kioskKey` alone used to be treated as
  // "fully set up" forever, which is exactly why an expired device zombied on the kiosk shell
  // instead of recovering. `expired` forces KioskSetup back onto the screen even though
  // `isSetupComplete` was already true.
  const expired = useKioskSessionStore((s) => s.expired);

  useEffect(() => {
    if (kioskKey) {
      setIsSetupComplete(true);
    }
  }, [kioskKey]);

  if (!isSetupComplete || expired) {
    return <KioskSetup onComplete={() => setIsSetupComplete(true)} />;
  }

  return (
    <>
      <KioskHubHandler />
      <div className={styles.root}>
        <main className={styles.content}>
          <Outlet />
        </main>
      </div>
    </>
  );
}

export function KioskLayout() {
  return (
    <KioskProvider>
      <KioskLayoutContent />
    </KioskProvider>
  );
}

export default KioskLayout;
